// ============================================================
// AuditLogPage — MEN-242
// Journal d'audit filtrable : user / action / date / IP,
// pagination serveur (cursor), export CSV, modal détail
// (JSON + diff before/after), live tail temps réel toggle.
// ============================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import type { AuditLog } from '@siverix/shared';
import type { AuditLogQuery } from '@siverix/api-client';
import { ErrorBox } from './OverviewPage';
import { useSocket } from '../hooks/useSocket';

const ACTIONS: string[] = [
  'community.create',
  'community.delete',
  'channel.create',
  'channel.delete',
  'member.kick',
  'member.ban',
  'member.unban',
  'role.create',
  'role.update',
  'role.delete',
  'message.delete',
  'user.disable',
  'user.enable',
  'instance.config.update',
  // [2.13.0] Serveur de comptes : blocage d'un serveur Siverix.
  'siverix_server.block',
  'siverix_server.unblock',
];

interface UserOption {
  id: string;
  username: string;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function AuditLogPage(): JSX.Element {
  const [rows, setRows] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // Filtres
  const [userId, setUserId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [userOpts, setUserOpts] = useState<UserOption[]>([]);
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [ip, setIp] = useState('');

  // Live tail
  const [liveTail, setLiveTail] = useState(true);
  const liveTailRef = useRef(liveTail);
  liveTailRef.current = liveTail;

  // Modal détail
  const [detail, setDetail] = useState<AuditLog | null>(null);

  const { socket } = useSocket();

  const filters: AuditLogQuery = useMemo(() => {
    const f: AuditLogQuery = {};
    if (userId) f.userId = userId;
    if (action) f.action = action;
    if (from) f.from = new Date(`${from}T00:00:00`).toISOString();
    if (to) f.to = new Date(`${to}T23:59:59.999`).toISOString();
    if (ip.trim()) f.ip = ip.trim();
    return f;
  }, [userId, action, from, to, ip]);

  const PAGE = 50;

  const fetchFirst = useCallback(async (): Promise<void> => {
    setLoading(true);
    setErr(null);
    try {
      const data = await api.adminAuditLog({ ...filters, limit: PAGE });
      setRows(data);
      setHasMore(data.length === PAGE);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void fetchFirst();
  }, [fetchFirst]);

  async function loadMore(): Promise<void> {
    const last = rows[rows.length - 1];
    if (!last) return;
    setLoadingMore(true);
    try {
      const data = await api.adminAuditLog({ ...filters, limit: PAGE, cursor: last.id });
      setRows((prev) => [...prev, ...data]);
      setHasMore(data.length === PAGE);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoadingMore(false);
    }
  }

  // Live tail : prepend les nouvelles entrées (si toggle actif et que
  // l'entrée passe les filtres côté client de façon best-effort).
  useEffect(() => {
    if (!socket) return;
    const onNew = (entry: AuditLog): void => {
      if (!liveTailRef.current) return;
      if (userId && entry.actorId !== userId) return;
      if (action && entry.action !== action) return;
      setRows((prev) => {
        if (prev.some((r) => r.id === entry.id)) return prev;
        return [entry, ...prev].slice(0, 1000);
      });
    };
    socket.on('audit:new', onNew);
    return () => {
      socket.off('audit:new', onNew);
    };
  }, [socket, userId, action]);

  // Autocomplete utilisateur (recherche serveur best-effort).
  useEffect(() => {
    let cancelled = false;
    const q = userSearch.trim();
    if (q.length < 2) {
      setUserOpts([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .adminListUsers(q)
        .then((us) => {
          if (!cancelled) setUserOpts(us.map((u) => ({ id: u.id, username: u.username })));
        })
        .catch(() => {
          if (!cancelled) setUserOpts([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [userSearch]);

  function resetFilters(): void {
    setUserId('');
    setUserSearch('');
    setUserOpts([]);
    setAction('');
    setFrom('');
    setTo('');
    setIp('');
  }

  async function exportCsv(): Promise<void> {
    try {
      const csv = await api.adminAuditLogCsv(filters);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Journal d'audit</h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
        Tous les événements de sécurité et d'administration. Filtrable, exportable, flux temps réel.
      </p>

      {/* ---- Filtres ---- */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
          marginBottom: '14px',
        }}
      >
        <label style={lbl}>
          Utilisateur
          <input
            list="audit-users"
            placeholder="username…"
            value={userSearch}
            onChange={(e) => {
              setUserSearch(e.target.value);
              const m = userOpts.find((o) => o.username === e.target.value);
              setUserId(m ? m.id : '');
            }}
            style={inp}
          />
          <datalist id="audit-users">
            {userOpts.map((o) => (
              <option key={o.id} value={o.username} />
            ))}
          </datalist>
        </label>

        <label style={lbl}>
          Action
          <select value={action} onChange={(e) => setAction(e.target.value)} style={inp}>
            <option value="">Toutes</option>
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>

        <label style={lbl}>
          Du
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inp} />
        </label>
        <label style={lbl}>
          Au
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inp} />
        </label>

        <label style={lbl}>
          IP
          <input
            placeholder="ex: 192.168."
            value={ip}
            onChange={(e) => setIp(e.target.value)}
            style={inp}
          />
        </label>

        <button onClick={resetFilters} style={btnGhost} type="button">
          Réinitialiser
        </button>
        <button onClick={() => void exportCsv()} style={btn} type="button">
          Exporter CSV
        </button>

        <label
          style={{
            ...lbl,
            flexDirection: 'row',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            color: 'var(--color-text)',
          }}
        >
          <input
            type="checkbox"
            checked={liveTail}
            onChange={(e) => setLiveTail(e.target.checked)}
          />
          Suivre en temps réel
        </label>
      </div>

      {err && <ErrorBox message={err} />}

      {loading ? (
        <div>Chargement…</div>
      ) : (
        <>
          <div
            style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '12px',
                fontFamily: 'ui-monospace, monospace',
              }}
            >
              <thead>
                <tr style={{ background: 'var(--color-bg)', textAlign: 'left' }}>
                  <th style={th}>Date</th>
                  <th style={th}>Acteur</th>
                  <th style={th}>Action</th>
                  <th style={th}>Cible</th>
                  <th style={th}>IP</th>
                  <th style={th} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const rowIp = isPlainObject(r.details)
                    ? ((r.details as Record<string, unknown>).ip as string | undefined)
                    : undefined;
                  return (
                    <tr key={r.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={td}>{new Date(r.createdAt).toLocaleString('fr-FR')}</td>
                      <td style={td}>{r.actor?.username ?? r.actorId}</td>
                      <td style={{ ...td, color: 'var(--color-accent)' }}>{r.action}</td>
                      <td style={td}>
                        {r.targetType ? `${r.targetType}:${r.targetId ?? '?'}` : '—'}
                      </td>
                      <td style={td}>{rowIp ?? '—'}</td>
                      <td style={td}>
                        <button onClick={() => setDetail(r)} style={btnGhost} type="button">
                          Détail
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        ...td,
                        color: 'var(--color-text-muted)',
                        textAlign: 'center',
                        padding: '24px',
                      }}
                    >
                      Aucun événement.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <button
                onClick={() => void loadMore()}
                disabled={loadingMore}
                style={btn}
                type="button"
              >
                {loadingMore ? 'Chargement…' : 'Charger plus'}
              </button>
            </div>
          )}
        </>
      )}

      {detail && <DetailModal log={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

// ---- Modal détail : JSON complet + diff before/after ----

function DetailModal({ log, onClose }: { log: AuditLog; onClose: () => void }): JSX.Element {
  const d = log.details;
  const hasDiff =
    isPlainObject(d) && 'before' in d && 'after' in d && log.action.includes('update');

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '10px',
          padding: '20px',
          maxWidth: '720px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '16px' }}>
            <span style={{ color: 'var(--color-accent)' }}>{log.action}</span>
          </h2>
          <button onClick={onClose} style={btnGhost} type="button">
            Fermer
          </button>
        </div>

        <dl style={{ fontSize: '13px', margin: '0 0 14px', lineHeight: 1.7 }}>
          <Row k="ID" v={log.id} />
          <Row k="Date" v={new Date(log.createdAt).toLocaleString('fr-FR')} />
          <Row k="Acteur" v={`${log.actor?.username ?? '?'} (${log.actorId})`} />
          <Row k="Cible" v={log.targetType ? `${log.targetType}:${log.targetId ?? '?'}` : '—'} />
          <Row
            k="IP"
            v={
              isPlainObject(log.details)
                ? String((log.details as Record<string, unknown>).ip ?? '—')
                : '—'
            }
          />
        </dl>

        {hasDiff ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <h3 style={diffH}>Avant</h3>
              <pre style={pre}>
                {JSON.stringify((d as Record<string, unknown>).before, null, 2)}
              </pre>
            </div>
            <div>
              <h3 style={diffH}>Après</h3>
              <pre style={pre}>{JSON.stringify((d as Record<string, unknown>).after, null, 2)}</pre>
            </div>
          </div>
        ) : (
          <div>
            <h3 style={diffH}>Métadonnées</h3>
            <pre style={pre}>
              {log.details ? JSON.stringify(log.details, null, 2) : 'Aucune métadonnée.'}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <dt style={{ color: 'var(--color-text-muted)', minWidth: '70px' }}>{k}</dt>
      <dd style={{ margin: 0, fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>
        {v}
      </dd>
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '10px 16px',
  fontWeight: 600,
  fontSize: '11px',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
};
const td: React.CSSProperties = { padding: '8px 16px' };
const lbl: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  fontSize: '11px',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
};
const inp: React.CSSProperties = {
  padding: '6px 8px',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  color: 'var(--color-text)',
  fontSize: '13px',
};
const btn: React.CSSProperties = {
  padding: '7px 14px',
  background: 'var(--color-accent)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  padding: '6px 12px',
  background: 'transparent',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  fontSize: '12px',
  cursor: 'pointer',
};
const diffH: React.CSSProperties = {
  fontSize: '12px',
  textTransform: 'uppercase',
  color: 'var(--color-text-muted)',
  margin: '0 0 6px',
};
const pre: React.CSSProperties = {
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  padding: '10px',
  fontSize: '12px',
  overflow: 'auto',
  margin: 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
};
